import { LitElement, html, css } from 'lit'
import routes from './routes.js'
import {
  homeIcon,
  voteIcon,
  executiveIcon,
  walletIcon,
  settingsIcon,
  kulaMenuIcon,
  accountIcon,
} from '../assets/icons.js'
import { go } from './router-mixin.js'
import AppEvents from '../../src/utilities/app-events.js'
import { getProposalCount } from '../../src/data/services/proposals.service.js'
import { featureFlags } from '../../src/utilities/feature-flags.js'
import { getMultiSigOwners } from '../../src/data/services/dao.service.js'

class MobileNavBarElement extends LitElement {
  static properties = {
    routes: { type: Object },
    currentLocation: { type: String },
    selectedAccount: { type: Object },
    selectedDao: { type: Object },
    activeProposalCount: { type: Number },
    executiveProposalCount: { type: Number },
    kipRoles: { type: Array },
    isDaoAdmin: { type: Boolean },
  }

  constructor() {
    super()
    this.currentLocation = window.location.pathname
    this.selectedDao = window.Globals.selectedDao
    this.selectedAccount = window.Globals.selectedAccount
    this.activeProposalCount = null
    this.executiveProposalCount = null
    this.kipRoles = window.Globals.kipRoles || []
    this.isDaoAdmin = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.checkIsDaoAdmin()
    window.addEventListener(
      AppEvents.ROUTE_CHANGE,
      (/** @type {CustomEvent} */ e) => {
        this.currentLocation = window.location.pathname
      }
    )

    window.Globals.onUpdate(
      ({ detail: { selectedAccount, selectedDao, kipRoles } }) => {
        this.selectedAccount = selectedAccount
        this.selectedDao = selectedDao
        this.kipRoles = kipRoles || []
        this.checkIsDaoAdmin()
        this.updateIndicator().catch(console.error)
      }
    )
  }

  async checkIsDaoAdmin() {
    if (this.selectedDao) {
      const owners = await getMultiSigOwners(this.selectedDao.multiSigAddress)
      this.isDaoAdmin = owners.some(
        (owner) =>
          owner.toLowerCase() ===
          this.selectedAccount.walletAddress.toLowerCase()
      )
    }
  }

  async updateIndicator() {
    try {
      if (this.selectedDao) {
        // TODO: this service call is not pointing at the new API endpoint
        this.activeProposalCount = await getProposalCount(this.selectedDao, 1)
        this.executiveProposalCount =
          (await getProposalCount(this.selectedDao, 4)) +
          (await getProposalCount(this.selectedDao, 5))
      }
    } catch (error) {
      console.error('Failed to update proposal count:', error)
      // TODO: under what conditions do we want 0 vs null. Default is 0 is that a different UI
      this.activeProposalCount = 0
    }
  }

  /**
   * @param {string} path
   * @param {string} currentLocation
   * @returns {boolean}
   * */
  isSelected(path, currentLocation) {
    return path === currentLocation
  }

  hideKipMenu() {
    if (!this.kipRoles || this.kipRoles.length === 0) {
      return true
    }
    return false
  }

  render() {
    const kipRoles = window.Globals.kipRoles || []
    const hideKipMenu = !kipRoles || kipRoles.length === 0
    const isAdmin = this.isDaoAdmin

    const items = [
      { path: routes.DASHBOARD.path, icon: homeIcon, text: 'Home' },
      {
        path: routes.MY_ACCOUNT.path,
        icon: accountIcon,
        text: 'Account',
      },
      {
        path: routes.VOTING.path,
        icon: voteIcon,
        text: 'Voting',
      },
      {
        path: routes.REDEEM.path,
        icon: walletIcon,
        text: 'Redeem',
        featureFlag: 'redeem',
      },
      {
        path: routes.KIP.path,
        icon: kulaMenuIcon,
        text: 'KIP',
        hide: hideKipMenu,
      },
      {
        path: routes.EXECUTIVE.path,
        icon: executiveIcon,
        text: 'Executive',
        hide: !isAdmin,
      },
    ].filter((item) => !item.featureFlag || featureFlags[item.featureFlag])

    return html`
      <div class="container">
        ${items.map(
          (item) => html`
            <span
              class="nav-item-wrapper ${this.isSelected(
                item.path,
                this.currentLocation
              )
                ? 'selected-item'
                : 'non-selected-item'}
                
                ${item.hide ? 'hide' : ''}"
            >
              ${item === items[2] && this.activeProposalCount > 0
                ? html`
                    <span class="indicator">${this.activeProposalCount}</span>
                  `
                : ''}
              ${item === items[4] && this.executiveProposalCount > 0
                ? html`
                    <span class="indicator"
                      >${this.executiveProposalCount}</span
                    >
                  `
                : ''}
              <div
                class="nav-item ${item.disable ? 'disabled' : ''}"
                ?selected=${this.isSelected(item.path, this.currentLocation)}
                @click=${item.disable ? null : go.bind(this, item.path)}
              >
                <span class="icon-wrapper">${item.icon}</span>
                <p class="text-wrapper">${item.text}</p>
              </div>
            </span>
          `
        )}
      </div>
    `
  }

  static styles = [
    css`
      :host {
        position: fixed;
        box-sizing: border-box;
        height: 32px;
        width: auto;
        display: flex;
        justify-content: flex-start;
        margin: 24px auto;
        background-color: #f7f7f7;
        border: 2px solid var(--light-grey);
        border-radius: 16px;
        left: 50%;
        transform: translateX(-50%);
        box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.2);
      }

      .container {
        width: 100%;
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        gap: 4px;
        transition: background-color 0.3s ease;
      }

      .nav-item-wrapper {
        position: relative;
      }

      .nav-item {
        width: 64px;
        display: flex;
        flex-direction: row;
        justify-content: left;
        align-items: center;
        gap: 4px;
        cursor: pointer;
        transition: color 0.3s ease;
      }

      .nav-item[selected] {
        color: var(--kula-light-green);
        transition: color 0.3s ease;
      }

      .nav-item[selected] > .icon-wrapper > svg {
        --icon-fill-color: var(--kula-light-green);
        transition: --icon-fill-color 0.3s ease;
      }

      .nav-item.disabled {
        color: var(--kula-grey-2);
        cursor: default;
      }

      .nav-item.disabled > svg {
        --icon-fill-color: var(--kula-grey-2);
      }

      svg {
        --icon-fill-color: var(--kula-deep-green);
        fill: var(--icon-fill-color);
        height: 18px;
        width: 18px;
        transition: --icon-fill-color 0.3s ease;
      }

      .selected-item {
        box-sizing: border-box;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: var(--kula-deep-green);
        color: white;
        width: 25vw;
        border-radius: 16px;
        padding: 12px;
        font-size: 12px;
        transition: background-color 0.3s ease;
      }

      .non-selected-item {
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: var(--kula-deep-green);
        border-radius: 16px;
        padding: 12px;
        font-size: 12px;
        transition: background-color 0.3s ease;
      }

      .indicator {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 10%;
        transform: translateY(-50%);
        right: -15px;
        width: 24px;
        height: 24px;
        border-radius: 100%;
        background: var(--kula-green);
        color: var(--White, #f7f7f7);
      }

      .hide {
        display: none;
      }

      /* Desktop styles */
      @media (min-width: 720px) {
        :host {
          margin: 0;
          padding: 0;
          left: 40px;
          top: 8px;
          height: 100vh;
          box-shadow: none;
          border-radius: 0;
        }

        .container {
          width: auto;
          height: 100%;
          margin-top: 80px;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          padding: 0;
        }

        .nav-item {
          color: var(--kula-grey-1);
          height: 40px;
        }

        .selected-item {
          padding: 8px;
          width: 100%;
          height: 40px;
          border-radius: 0;
          background-color: var(--kula-grey-3);
          border-left: 8px solid var(--kula-deep-green);
        }

        .non-selected-item {
          height: 40px;
        }

        .nav-item[selected] {
          color: var(--kula-deep-green);
        }

        .nav-item[selected] > .icon-wrapper > svg {
          --icon-fill-color: var(--kula-deep-green);
        }

        svg {
          margin: auto;
        }

        .text-wrapper {
          display: flex;
        }
      }

      /* Mobile styles */
      @media (max-width: 720px) {
        .selected-item,
        .non-selected-item {
          padding: 8px;
          height: 24px;
          justify-content: center;
          align-items: center;
          width: calc(100% - 24px);
        }

        .nav-item {
          width: 100%;
          padding: 0;
        }

        .icon-wrapper {
          display: none;
        }
      }
    `,
  ]
}
customElements.define('mobile-nav-bar', MobileNavBarElement)
export default MobileNavBarElement
